import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@jobenvoy/core";
import { useSession } from "@/auth/session";
import { getConsents, getMe, recordConsent, type ConsentRecord } from "@/lib/api/auth";
import { keys } from "./keys";
import { ONE_AT_A_TIME, useSingleFlightMutation } from "./singleFlight";

/**
 * What the signed-in person agreed to, against the versions in force.
 *
 * The consent gate stands in front of every signed-in page, so this is the
 * first read of a session, and it asks organizations who the person is
 * first (`GET /v1/users/me`, once a session): that is what makes their
 * record the first time they sign in, and organizations keeps no consents
 * for a person it has no record of.
 */
export function useConsents() {
    const { userId } = useSession();
    const client = useQueryClient();
    return useQuery({
        queryKey: keys.auth.consents(userId),
        queryFn: async () => {
            await client.ensureQueryData({
                queryKey: keys.auth.me(userId),
                queryFn: getMe,
                staleTime: Number.POSITIVE_INFINITY,
            });
            return getConsents(userId);
        },
    });
}

/**
 * Adds one record, one at a time. The answer is where the person now
 * stands, and replaces the read. A 412 means the versions moved on, so
 * the read is taken again.
 */
export function useRecordConsent() {
    const { userId } = useSession();
    const client = useQueryClient();
    return useSingleFlightMutation(
        {
            mutationFn: (record: ConsentRecord) => recordConsent(userId, record),
            onSuccess: (consents) => client.setQueryData(keys.auth.consents(userId), consents),
            onError: (error) => {
                if (error instanceof ApiError && error.status === 412)
                    void client.invalidateQueries({ queryKey: keys.auth.consents(userId) });
            },
        },
        ONE_AT_A_TIME,
    );
}
